import { useLayoutEffect } from 'react';
import { createFileRoute, Outlet, useRouterState } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { PageBody } from '@/components/layout/PageBody';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import {
  AnalysisSetupHeader,
  PIPELINE_STEPS,
  SetupStepChrome,
  type PipelineStepId,
} from '@/features/analysis-setup';

export const Route = createFileRoute('/analysis-setup')({
  component: AnalysisSetupLayout,
});

/**
 * Route → step, derived from the catalog rather than restated.
 *
 * Both halves used to be written out by hand here, so a fifth step added to
 * `PIPELINE_STEPS` would appear in the strip and the overview map but get no
 * active state and no `StepIntro` on its own screen — visible, and silently
 * unlike its neighbours.
 */
const STEP_BY_PATH: Record<string, PipelineStepId> = Object.fromEntries(
  PIPELINE_STEPS.map((step) => [step.to, step.id])
);

function AnalysisSetupLayout() {
  const { t } = useTranslation(['analysisSetup', 'common']);
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const activeStep = STEP_BY_PATH[pathname.replace(/\/$/, '')];
  const activeStepMeta = PIPELINE_STEPS.find((step) => step.id === activeStep);
  // The tab and the page's `h1` name the same thing: the step on a step screen.
  useDocumentTitle(
    activeStepMeta ? t(activeStepMeta.labelKey) : t('analysisSetup:title')
  );
  // The shell's `<main>` is the scroll region and outlives the route: without
  // this a step opens scrolled to wherever the overview (or the previous step)
  // was left. Keyed on the path, so switching a tab inside a step keeps its place.
  useLayoutEffect(() => {
    document.getElementById('main-content')?.scrollTo(0, 0);
  }, [pathname]);

  if (activeStepMeta) {
    return (
      <SetupStepChrome step={activeStepMeta}>
        <Outlet />
      </SetupStepChrome>
    );
  }

  // The overview is one scrolling page: its own title, then the whole path.
  // No step strip here — the overview draws every step itself, and the strip
  // used to repeat the same steps a second time right above it (#1628).
  return (
    <PageBody data-testid="analysis-setup-page">
      <AnalysisSetupHeader subtitle={t('analysisSetup:description')} />
      <Outlet />
    </PageBody>
  );
}
